import { API_BASE } from '../config/api';
import i18n from '../i18n';

// Internal starting-point type keys. These are the language-independent
// identity of a point — all control flow (icon choice, saved-point lookup,
// URL bridging via `startingPointType`) keys on `type`, NEVER on the
// display `name`, which is free to localize. The 'current-location'
// spelling matches the URL param value already in shareable links.
export const STARTING_POINT_TYPES = ['current-location', 'home', 'work', 'hotel', 'custom'];

// Saved points created before `type` existed carry only their (English)
// display name — the predefined setup flow prefilled the form with
// "Home"/"Work"/"Hotel". Used once per point by the read-path migration in
// getStartingPoints(); anything unrecognized (user-typed names) is 'custom'.
const LEGACY_NAME_TO_TYPE = {
  'current location': 'current-location',
  'home': 'home',
  'work': 'work',
  'hotel': 'hotel'
};

class StartingPointService {
  static saveStartingPoint(name, address, coordinates, isDefault = false, type = 'custom') {
    const startingPoints = this.getStartingPoints();
    const newPoint = {
      id: Date.now().toString(),
      name,
      address,
      coordinates, // { lat, lng }
      type,
      isDefault,
      createdAt: new Date().toISOString()
    };

    // If this is default, unset others
    if (isDefault) {
      startingPoints.forEach(point => point.isDefault = false);
    }

    startingPoints.push(newPoint);
    localStorage.setItem('startingPoints', JSON.stringify(startingPoints));
    return newPoint;
  }

  static getStartingPoints() {
    try {
      const points = JSON.parse(localStorage.getItem('startingPoints') || '[]');
      // Read-path migration: stamp `type` on legacy points saved before the
      // field existed. Legacy names are guaranteed English (the app predates
      // localization), so the name→type inference is safe exactly once here.
      let migrated = false;
      for (const point of points) {
        if (!point.type) {
          point.type = LEGACY_NAME_TO_TYPE[(point.name || '').toLowerCase()] || 'custom';
          migrated = true;
        }
      }
      if (migrated) {
        localStorage.setItem('startingPoints', JSON.stringify(points));
      }
      return points;
    } catch (error) {
      console.error('Error loading starting points:', error);
      return [];
    }
  }

  static updateStartingPoint(id, updates) {
    const startingPoints = this.getStartingPoints();
    const index = startingPoints.findIndex(point => point.id === id);
    
    if (index >= 0) {
      // If setting as default, unset others
      if (updates.isDefault) {
        startingPoints.forEach(point => point.isDefault = false);
      }
      
      startingPoints[index] = { ...startingPoints[index], ...updates };
      localStorage.setItem('startingPoints', JSON.stringify(startingPoints));
      return startingPoints[index];
    }
    
    return null;
  }

  static deleteStartingPoint(id) {
    const startingPoints = this.getStartingPoints();
    const filtered = startingPoints.filter(point => point.id !== id);
    localStorage.setItem('startingPoints', JSON.stringify(filtered));
    return filtered;
  }

  static getDefaultStartingPoint() {
    const points = this.getStartingPoints();
    return points.find(p => p.isDefault) || points[0] || null;
  }

  static setDefaultStartingPoint(id) {
    const startingPoints = this.getStartingPoints();
    startingPoints.forEach(point => {
      point.isDefault = (point.id === id);
    });
    localStorage.setItem('startingPoints', JSON.stringify(startingPoints));
  }

  // Display strings resolve from labels.json via lazy getters (I18N.md shared
  // label modules pattern) — same `.name` access for consumers, resolved at
  // render time in the active language, never at module load.
  static getPredefinedStartingPoints() {
    return [
      {
        type: 'current-location',
        id: 'current',
        coordinates: null,
        icon: '📍',
        get name() { return i18n.t('labels:startingPoints.currentLocation.name'); },
        get description() { return i18n.t('labels:startingPoints.currentLocation.description'); }
      },
      {
        type: 'home',
        id: 'home',
        coordinates: null,
        icon: '🏠',
        get name() { return i18n.t('labels:startingPoints.home.name'); },
        get description() { return i18n.t('labels:startingPoints.home.description'); }
      },
      {
        type: 'work',
        id: 'work',
        coordinates: null,
        icon: '🏢',
        get name() { return i18n.t('labels:startingPoints.work.name'); },
        get description() { return i18n.t('labels:startingPoints.work.description'); }
      },
      {
        type: 'hotel',
        id: 'hotel',
        coordinates: null,
        icon: '🏨',
        get name() { return i18n.t('labels:startingPoints.hotel.name'); },
        get description() { return i18n.t('labels:startingPoints.hotel.description'); }
      }
    ];
  }

  // Get current location using browser geolocation
  static async getCurrentLocation() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    });
  }

  // Geocode an address to get coordinates
  static async geocodeAddress(address) {
    try {
      // Use Google Geocoding API through our proxy
      const response = await fetch(`${process.env.REACT_APP_PROXY_URL || `${API_BASE}/api/places`}/geocode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address })
      });

      const data = await response.json();
      
      if (data.status === 'success' && data.results && data.results.length > 0) {
        const location = data.results[0].geometry.location;
        return {
          lat: typeof location.lat === 'function' ? location.lat() : location.lat,
          lng: typeof location.lng === 'function' ? location.lng() : location.lng,
          formatted_address: data.results[0].formatted_address
        };
      }
      
      throw new Error('Unable to geocode address');
    } catch (error) {
      console.error('Geocoding error:', error);
      // Fallback: return null coordinates but keep the address
      return null;
    }
  }

  // Reverse geocode coordinates to get address
  static async reverseGeocode(coordinates) {
    try {
      const response = await fetch(`${process.env.REACT_APP_PROXY_URL || `${API_BASE}/api/places`}/reverse-geocode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lat: coordinates.lat, lng: coordinates.lng })
      });

      const data = await response.json();
      
      if (data.status === 'success' && data.results && data.results.length > 0) {
        return data.results[0].formatted_address;
      }
      
      return `${coordinates.lat.toFixed(4)}, ${coordinates.lng.toFixed(4)}`;
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      return `${coordinates.lat.toFixed(4)}, ${coordinates.lng.toFixed(4)}`;
    }
  }

  // Validate a starting point has required data
  static validateStartingPoint(startingPoint) {
    if (!startingPoint) {
      return { isValid: false, error: 'No starting point provided' };
    }

    if (!startingPoint.coordinates || !startingPoint.coordinates.lat || !startingPoint.coordinates.lng) {
      return { isValid: false, error: 'Starting point missing coordinates' };
    }

    return { isValid: true };
  }

  // Get saved starting point by internal type key ('home', 'work', 'hotel').
  // Matches on `type` — never on the display name, which is user-editable
  // and localized. getStartingPoints() stamps `type` on legacy points.
  static getStartingPointByType(type) {
    const points = this.getStartingPoints();
    return points.find(point => point.type === type);
  }

  // Search for starting points by name or address
  static searchStartingPoints(query) {
    const points = this.getStartingPoints();
    const queryLower = query.toLowerCase();
    
    return points.filter(point => 
      point.name.toLowerCase().includes(queryLower) ||
      point.address.toLowerCase().includes(queryLower)
    );
  }
}

export default StartingPointService;